import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

// MD3 buttons: Filled / Tonal / Outlined / Text / FAB, all pill-shaped.
// State layers: `state-layer-solid` (filter:brightness) on filled surfaces,
// `state-layer-transparent` (currentColor wash) on transparent ones — see
// index.css for the contract (ported from frontend/'s material.css). Focus
// is the standard MD3 ring on every interactive element.
const buttonVariants = cva(
  'state-layer-solid inline-flex items-center justify-center gap-2 rounded-full whitespace-nowrap text-sm font-medium transition-all duration-300 ease-emphasized-decelerate outline-none shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-safe:active:scale-95 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:shadow-md',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:shadow-md',
        secondary: 'bg-secondary text-secondary-foreground',
        outline: 'state-layer-transparent border border-outline bg-transparent text-primary',
        ghost: 'state-layer-transparent border border-transparent bg-transparent text-primary',
        tertiary: 'bg-tertiary text-on-tertiary shadow-sm hover:shadow-md',
        fab: 'bg-tertiary text-on-tertiary rounded-2xl shadow-lg hover:shadow-xl',
      },
      size: {
        default: 'h-10 px-6 has-[>svg]:px-5',
        sm: 'h-8 gap-1.5 px-4 has-[>svg]:px-3',
        lg: 'h-12 px-8 has-[>svg]:px-6',
        icon: 'size-10',
        'icon-sm': 'size-8',
        'icon-lg': 'size-12',
        fab: 'size-14 [&_svg:not([class*="size-"])]:size-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

// forwardRef: React 18 (unlike frontend/'s React 19) drops a `ref` prop
// silently on a plain function component instead of forwarding it.
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & VariantProps<typeof buttonVariants>
>(({ className, variant, size, ...props }, ref) => {
  return (
    <button
      ref={ref}
      data-slot="button"
      data-variant={variant}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});
Button.displayName = 'Button';

export { Button, buttonVariants };
